import React, { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { ApolloError, useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { up } from 'styled-breakpoints';
import styled from 'styled-components';
import {
  AiAgentKnowledgeBase,
  AiAgentKnowledgeBaseOrderByInput,
  AiAgentKnowledgeBaseReferenceSource,
  AiAgentsNamespaceGetKnowledgeBaseArgs,
  AiAgentsNamespaceLinkKnowledgeBaseArgs,
  AiAgentsNamespaceUnlinkKnowledgeBaseArgs,
  Mutation,
  Query,
} from '@lgg/isomorphic/types/__generated__/graphql';
import { TablePagination } from 'src/components/general/display/table-pagination';
import { useShowConfirmationModal } from 'src/components/general/feedback/hooks/use-show-confirmation-modal';
import { useShowNotification } from 'src/components/general/feedback/hooks/use-show-notification';
import { TableBodyPlaceholder } from 'src/components/general/feedback/loading-placeholders';
import { Icon } from 'src/components/general/icon';
import { Checkbox } from 'src/components/general/inputs/checkbox';
import { FlexRow } from 'src/components/layout/flex-row';
import { KnowledgeSourceTableFrame } from 'src/components/pages/ai-agents/components/ai-agents-table-columns';
import {
  ConfirmationModalDeleteFooter,
  ConfirmationModalMessage,
  ConfirmationModalTitle,
  useConfirmationModalSize,
} from 'src/components/pages/ai-agents/components/confirmation-modal-parts';
import { InUseItemsList } from 'src/components/pages/ai-agents/components/in-use-items-list';
import {
  KnowledgeEmptyState,
  KnowledgeSourceContainer,
} from 'src/components/pages/ai-agents/components/knowledge-tabs';
import { useAiAgentsAccess } from 'src/components/pages/ai-agents/hooks/use-ai-agents-access';
import { AiAgentsListVariables } from 'src/components/pages/ai-agents/hooks/use-ai-agents-list-query-params';
import { useKnowledgeSourceListState } from 'src/components/pages/ai-agents/hooks/use-knowledge-source-list-state';
import {
  KNOWLEDGE_BASE_USAGE_QUERY,
  KNOWLEDGE_BASES_LIST_QUERY,
  LINK_KNOWLEDGE_BASE_MUTATION,
  UNLINK_KNOWLEDGE_BASE_MUTATION,
} from 'src/components/pages/ai-agents/knowledge-bases/queries';
import { useAuthorization } from 'src/hooks/use-authorization';
import { useCurrentInstitution } from 'src/hooks/use-current-institution';
import { useHandleGraphQLError } from 'src/hooks/use-handle-graphql-error';

const Intro = styled.div`
  color: ${({ theme }) => theme.colors.smalt};
  font-family: ${({ theme }) => theme.font.medium};
  font-size: 14px;
  line-height: 19px;
  margin-bottom: 20px;

  p {
    margin: 0 0 1px;
  }

  p + p {
    color: ${({ theme }) => theme.colors.flint};
    font-family: ${({ theme }) => theme.font.regular};
    margin: 0;
  }
`;

const EditButton = styled.button`
  align-items: center;
  background: none;
  border: 1px solid ${({ theme }) => theme.colors.koala};
  border-radius: 4px;
  color: ${({ theme }) => theme.colors.flint};
  cursor: pointer;
  display: none;
  flex-shrink: 0;
  font-family: ${({ theme }) => theme.font.medium};
  font-size: 12px;
  gap: 10px;
  height: 30px;
  line-height: 18px;
  margin-left: auto;
  padding: 6px 15px;

  svg {
    height: 14px;
    width: 14px;

    path {
      fill: ${({ theme }) => theme.colors.flint};
    }
  }
`;

const Row = styled(FlexRow)`
  align-items: center;
  background: ${({ theme }) => theme.colors.white};
  border-bottom: 1px solid ${({ theme }) => theme.colors.porcelain};
  gap: 15px;
  min-height: 60px;
  padding: 15px 20px 15px 15px;

  ${up('md')} {
    height: 60px;
  }

  &:last-child {
    border-bottom: none;
  }

  ${up('md')} {
    &:hover {
      background: ${({ theme }) => theme.colors.alabaster};

      ${EditButton} {
        display: flex;
      }
    }
  }

  .ant-checkbox-inner {
    border-color: ${({ theme }) => theme.colors.casper};
    border-radius: 2px;
  }
`;

const TitleGroup = styled(FlexRow)`
  align-items: center;
  font-size: 13px;
  gap: 6px;
  line-height: 16px;
  min-width: 0;
`;

const Title = styled.span`
  color: ${({ theme }) => theme.colors.smalt};
  font-family: ${({ theme }) => theme.font.medium};
`;

const SourcesCount = styled.span`
  color: ${({ theme }) => theme.colors.flint};
  font-family: ${({ theme }) => theme.font.regular};
`;

type LinkedKnowledgeBasesVariables =
  AiAgentsListVariables<AiAgentKnowledgeBaseOrderByInput>;

export const LinkedKnowledgeBases = memo<{
  aiAgentId: string;
  sectionUrl: string;
}>(({ aiAgentId, sectionUrl }) => {
  const { t } = useTranslation(['aiAgents']);
  const history = useHistory();
  const { id: companyId } = useCurrentInstitution();
  const { hasPermission } = useAuthorization();
  const { canViewAiKnowledgeBases } = useAiAgentsAccess();
  const handleGraphQLError = useHandleGraphQLError();
  const showConfirmationModal = useShowConfirmationModal();
  const showNotification = useShowNotification();
  const [queryUsage] = useLazyQuery<
    Pick<Query, 'aiAgents'>,
    AiAgentsNamespaceGetKnowledgeBaseArgs
  >(KNOWLEDGE_BASE_USAGE_QUERY, { fetchPolicy: 'network-only' });
  const modalSize = useConfirmationModalSize();
  const canUpdate = hasPermission('ai:company.ai_agent:update:all');
  const canCreate = hasPermission('ai:company.ai_agent_knowledge_base:create');
  const [checkingUsage, setCheckingUsage] = useState(false);
  const { listVariables, getPaginationProps } =
    useKnowledgeSourceListState<AiAgentKnowledgeBase>();

  const variables: LinkedKnowledgeBasesVariables = {
    companyId,
    ...listVariables,
    orderBy: { title: 'ASC' },
  };

  const { data, loading } = useQuery<
    Pick<Query, 'aiAgents'>,
    LinkedKnowledgeBasesVariables
  >(KNOWLEDGE_BASES_LIST_QUERY, { variables, onError: handleGraphQLError });

  const mutationOptions = {
    onError: handleGraphQLError,
    refetchQueries: ['KnowledgeBasesList'],
    awaitRefetchQueries: true,
  };

  const [linkKnowledgeBase, { loading: linking }] = useMutation<
    Pick<Mutation, 'aiAgents'>,
    AiAgentsNamespaceLinkKnowledgeBaseArgs
  >(LINK_KNOWLEDGE_BASE_MUTATION, mutationOptions);

  const [unlinkKnowledgeBase, { loading: unlinking }] = useMutation<
    Pick<Mutation, 'aiAgents'>,
    AiAgentsNamespaceUnlinkKnowledgeBaseArgs
  >(UNLINK_KNOWLEDGE_BASE_MUTATION, {
    ...mutationOptions,
    onCompleted: () =>
      showNotification({
        type: 'success',
        title: t('aiAgents:knowledge.linkedKnowledgeBases.unlink.success'),
      }),
  });

  const confirmUnlink = async (knowledgeBaseId: string) => {
    let sources: AiAgentKnowledgeBaseReferenceSource[];

    setCheckingUsage(true);

    try {
      const { data } = await queryUsage({
        variables: { input: { companyId, id: knowledgeBaseId } },
      });
      sources =
        data?.aiAgents.getKnowledgeBase?.usage.references.nodes.find(
          ({ agent }) => agent.id === aiAgentId,
        )?.sources ?? [];
    } catch (error) {
      if (!(error instanceof ApolloError)) {
        throw error;
      }

      handleGraphQLError(error);

      return;
    } finally {
      setCheckingUsage(false);
    }

    if (sources.length > 0) {
      showConfirmationModal({
        testId: 'unlink-blocked-knowledge-base-modal',
        ...modalSize,
        title: (
          <ConfirmationModalTitle>
            {t('aiAgents:knowledge.linkedKnowledgeBases.blocked.title')}
          </ConfirmationModalTitle>
        ),
        message: (
          <InUseItemsList
            testId="unlink-blocked-message"
            intro={t('aiAgents:knowledge.linkedKnowledgeBases.blocked.intro')}
            items={sources}
          >
            <p>{t('aiAgents:knowledge.linkedKnowledgeBases.blocked.removeFirst')}</p>
          </InUseItemsList>
        ),
        footerContent: () => null,
      });

      return;
    }

    showConfirmationModal({
      testId: 'unlink-knowledge-base-modal',
      ...modalSize,
      title: (
        <ConfirmationModalTitle>
          {t('aiAgents:knowledge.linkedKnowledgeBases.unlink.title')}
        </ConfirmationModalTitle>
      ),
      message: (
        <ConfirmationModalMessage>
          <p>{t('aiAgents:knowledge.linkedKnowledgeBases.unlink.notInUse')}</p>
          <p>{t('aiAgents:knowledge.linkedKnowledgeBases.unlink.effect')}</p>
          <p>{t('aiAgents:knowledge.linkedKnowledgeBases.unlink.again')}</p>
        </ConfirmationModalMessage>
      ),
      footerContent: ({ onClose }) => (
        <ConfirmationModalDeleteFooter
          confirmLabel={t('aiAgents:knowledge.linkedKnowledgeBases.unlink.confirm')}
          onClose={onClose}
          onConfirm={() =>
            void unlinkKnowledgeBase({
              variables: { input: { companyId, aiAgentId, knowledgeBaseId } },
            })
          }
        />
      ),
    });
  };

  if (loading && !data) {
    return <TableBodyPlaceholder />;
  }

  if (!data) {
    return null;
  }

  const { nodes, totalCount, pageInfo } = data.aiAgents.listKnowledgeBases;

  if (totalCount === 0) {
    return (
      <KnowledgeSourceContainer>
        <KnowledgeEmptyState
          testId="linked-knowledge-bases-empty"
          icon="document"
          title={t('aiAgents:knowledge.linkedKnowledgeBases.empty.title')}
          message={t('aiAgents:knowledge.linkedKnowledgeBases.empty.message')}
          buttonLabel={
            canCreate && canViewAiKnowledgeBases
              ? t('aiAgents:knowledge.linkedKnowledgeBases.empty.button')
              : undefined
          }
          onAdd={() => history.push(`${sectionUrl}/knowledge-bases`)}
        />
      </KnowledgeSourceContainer>
    );
  }

  return (
    <KnowledgeSourceContainer data-lgg-id="linked-knowledge-bases">
      <Intro>
        <p>{t('aiAgents:knowledge.linkedKnowledgeBases.intro')}</p>
        <p>{t('aiAgents:knowledge.linkedKnowledgeBases.introHint')}</p>
      </Intro>
      <KnowledgeSourceTableFrame>
        {nodes.map(({ id, title, sourcesCount, aiAgents }) => {
          const isLinked = aiAgents.some((agent) => agent.id === aiAgentId);

          return (
            <Row key={id} data-lgg-id="linked-knowledge-base-row">
              <Checkbox
                checked={isLinked}
                disabled={!canUpdate || checkingUsage || linking || unlinking}
                data-lgg-id={`linked-knowledge-base-checkbox-${id}`}
                onChange={() =>
                  void (isLinked
                    ? confirmUnlink(id)
                    : linkKnowledgeBase({
                        variables: {
                          input: { companyId, aiAgentId, knowledgeBaseId: id },
                        },
                      }))
                }
              />
              <TitleGroup>
                <Title>{title}</Title>
                <SourcesCount>
                  {t('aiAgents:knowledge.linkedKnowledgeBases.sources', {
                    count: sourcesCount,
                  })}
                </SourcesCount>
              </TitleGroup>
              {canViewAiKnowledgeBases && (
                <EditButton
                  type="button"
                  data-lgg-id="linked-knowledge-base-edit"
                  onClick={() => history.push(`${sectionUrl}/knowledge-bases/${id}`)}
                >
                  <Icon type="externalLink" />
                  {t('aiAgents:knowledge.linkedKnowledgeBases.edit')}
                </EditButton>
              )}
            </Row>
          );
        })}
      </KnowledgeSourceTableFrame>
      {(pageInfo.hasPreviousPage || pageInfo.hasNextPage) && (
        <TablePagination {...getPaginationProps(pageInfo)} />
      )}
    </KnowledgeSourceContainer>
  );
});
